/**
 * Frontend display registry for vendor_imports providers.
 *
 * The backend (apps/vendor_imports/registry.py) is the source of truth for
 * `display_name` and `kind` — those flow through the API and are rendered
 * verbatim.  This registry only handles **frontend-bundled assets** (icon
 * paths), which the backend deliberately does not know about.
 *
 * Adding a new provider
 * ---------------------
 * 1. Register it in the backend (apps/vendor_imports/registry.py +
 *    apps/vendor_imports/provider_names.py).
 * 2. Add the provider name to `ImportProviderName` below.
 * 3. Add an entry to `PROVIDER_DISPLAY_REGISTRY` with an SVG path under
 *    `/assets/figma/`.
 * 4. The `__fallback` entry is used if the API returns a provider name that
 *    is not in this registry — the UI keeps rendering, just without a vendor
 *    icon, so missing-registry-entry bugs never break the page.  We
 *    deliberately do NOT reuse a vendor-specific asset (e.g. the
 *    test-integration icon) as the fallback because that would mislabel
 *    unknown providers as the dev/QA test integration.
 */

export type ImportProviderName =
  | 'gmail'
  | 'gcal'
  | 'testemail'
  | 'testcalendar'
  | 'outlook_mail'
  | 'outlook_calendar'
  | 'salesforce';

export interface ProviderDisplayMetadata {
  /**
   * Path under /public — must exist and be served as a static asset.
   * `null` means the frontend has no icon for this provider; consumers
   * should render a neutral placeholder instead of a vendor logo.
   */
  iconPath: string | null;
}

interface ProviderRegistry extends Record<
  ImportProviderName,
  ProviderDisplayMetadata
> {
  /** Neutral fallback used when the API returns an unknown provider name. */
  __fallback: ProviderDisplayMetadata;
}

export const PROVIDER_DISPLAY_REGISTRY: ProviderRegistry = {
  gmail: { iconPath: '/assets/figma/gmail-icon.svg' },
  gcal: { iconPath: '/assets/figma/google-calendar-icon.svg' },
  testemail: { iconPath: '/assets/figma/test-integration-icon.svg' },
  testcalendar: { iconPath: '/assets/figma/test-integration-icon.svg' },
  outlook_mail: { iconPath: '/assets/figma/outlook-icon.svg' },
  outlook_calendar: { iconPath: '/assets/figma/outlook-calendar-icon.svg' },
  salesforce: { iconPath: '/assets/figma/salesforce-icon.svg' },
  __fallback: { iconPath: null },
};
